// src/api/ConfigApi.js
import axios from 'axios';

// Assuming you have a shared error handler utility like handleApiError
// If not, copy/adapt the implementation from ProductApi.js or another API file
import { handleApiError } from './handleApiError.js'; // Example import path

const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
const configApiUrl = `${baseUrl}/config/api`;


/** Fetches the CURRENT USER's effective email task templates (merged defaults + overrides). */
export const fetchTaskTemplates = async (token) => {
    const url = `${configApiUrl}/task-templates`;
    console.debug("[fetchTaskTemplates] Attempting fetch for current user...");
    try {
        const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` }, validateStatus: (s) => s < 500 });
        if (response.status === 200) {
            // FIX: First, explicitly check if the data is an array.
            // If not, default to an empty array. This handles cases where the
            // backend returns an error object or string instead of an empty array.
            const dataArray = Array.isArray(response.data) ? response.data : [];

            // Now, it's completely safe to process the array.
            const templates = dataArray
                .filter(tmpl => tmpl && typeof tmpl === 'object')
                .map(tmpl => ({
                    ...tmpl,
                    prompt: tmpl.prompt ?? '',
                    followUpText: tmpl.followUpText ?? '',
                    followUpPrompt: tmpl.followUpPrompt ?? '',
                    followUpDaysOffset: tmpl.followUpDaysOffset ?? '',
                }));
    
    
            console.log("[fetchTaskTemplates] API CLIENT: Data before returning to context", templates.map(t => t.taskType)); 
            return { status: true, data: templates };
        } else { return handleApiError({ response }, 'fetchTaskTemplates'); }
    } catch (error) { return handleApiError(error, 'fetchTaskTemplates'); }
};


/** Creates or updates the CURRENT USER's override for a specific task template. */
export const updateTaskTemplate = async (token, taskType, updateData) => {
    // updateData should contain ONLY { prompt?, followUpText?, followUpPrompt?, followUpDaysOffset? }
    const url = `${configApiUrl}/task-templates/${taskType}`;
    console.debug(`[updateTaskTemplate] Attempting update override for ${taskType}...`);
    try {
        const payload = { // Send only allowed fields
            prompt: updateData.prompt,
            follow_up_text: updateData.followUpText,
            follow_up_prompt: updateData.followUpPrompt,
            follow_up_days_offset: updateData.followUpDaysOffset,
        };
        const response = await axios.put(url, payload, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, validateStatus: (s) => s < 500 });
        if (response.status === 200) { return { status: true, message: response.data.message }; }
        else { return handleApiError({ response }, 'updateTaskTemplate'); }
    } catch (error) { return handleApiError(error, 'updateTaskTemplate'); }
};


/**
 * Fetches available LLM configurations grouped by provider and idiom.
 */
export const fetchLlmConfigurations = async (token) => {
    const url = `${configApiUrl}/llm-configurations`;
    console.debug("[fetchLlmConfigurations] Attempting fetch...");
    try {
        const response = await axios.get(url, {
            headers: { Authorization: `Bearer ${token}` },
            validateStatus: (status) => status >= 200 && status < 500,
        });
        if (response.status === 200) {
            // Backend returns { provider: { idiom: [models] } }
            return { status: true, data: response.data };
        } else {
            return handleApiError({ response }, 'fetchLlmConfigurations');
        }
    } catch (error) {
        return handleApiError(error, 'fetchLlmConfigurations');
    }
};

/**
 * Fetches the current user's settings.
 */
export const fetchUserSettings = async (token) => {
    const url = `${configApiUrl}/user-settings`;
    console.debug("[fetchUserSettings] Attempting fetch...");
    try {
        const response = await axios.get(url, {
            headers: { Authorization: `Bearer ${token}` },
            validateStatus: (status) => status >= 200 && status < 500,
        });
        if (response.status === 200) {
            return {
                status: true,
                data: response.data
             };
        } else {
            return handleApiError({ response }, 'fetchUserSettings');
        }
    } catch (error) {
        return handleApiError(error, 'fetchUserSettings');
    }
};

/**
 * Updates the current user's settings.
 */
export const updateUserSettings = async (token, settingsUpdate) => {
    // settingsUpdate should be like { defaultLlmConfigId: 123 | null }
    const url = `${configApiUrl}/user-settings`;
    console.debug("[updateUserSettings] Attempting update...");
    try {
        const payload = {
            defaultLlmConfigId: settingsUpdate.defaultLlmConfigId
        };
        const response = await axios.put(url, payload, {
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            validateStatus: (status) => status >= 200 && status < 500,
        });
        if (response.status === 200) {
            return { status: true, message: response.data.message };
        } else {
            return handleApiError({ response }, 'updateUserSettings');
        }
    } catch (error) {
        return handleApiError(error, 'updateUserSettings');
    }
};

/**
 * Fetches whether an API key exists for the current user and a specific provider.
 * Note: Does NOT return the key itself for security.
 * @param {string} token - Firebase Auth ID token.
 * @param {string} providerName - The name of the LLM provider.
 * @returns {Promise<{status: boolean, data: {apiKeyExists: boolean}}|{status: boolean, message: string, error?: any}>}
 */
export const fetchUserApiKeyStatus = async (token, providerName) => {
    const url = `${configApiUrl}/user-api-key/${encodeURIComponent(providerName)}`;
    console.debug(`[fetchUserApiKeyStatus] Attempting fetch for provider: ${providerName}`);
    try {
        const response = await axios.get(url, {
            headers: { Authorization: `Bearer ${token}` },
            validateStatus: (status) => status >= 200 && status < 500, // Allow 404 too
        });

        // Backend now returns 200 OK with { apiKeyExists: true/false }
        if (response.status === 200) {
            console.debug(`[fetchUserApiKeyStatus] Success for provider ${providerName}. Exists: ${response.data.apiKeyExists}`);
            return { status: true, data: { apiKeyExists: response.data.apiKeyExists } };
        } else {
            // Other errors (e.g., 500)
             return handleApiError({ response }, 'fetchUserApiKeyStatus');
        }
    } catch (error) {
         return handleApiError(error, 'fetchUserApiKeyStatus');
    }
};


/**
 * Creates or updates the API key for the current user and a specific provider.
 * @param {string} token - Firebase Auth ID token.
 * @param {string} providerName - The name of the LLM provider.
 * @param {string} apiKey - The plain text API key to save.
 * @returns {Promise<{status: boolean, message: string}|{status: boolean, message: string, error?: any}>}
 */
export const upsertUserApiKey = async (token, providerName, apiKey) => {
    const url = `${configApiUrl}/user-api-key`;
    console.debug(`[upsertUserApiKey] Attempting upsert for provider: ${providerName}`);
    try {
        const payload = { providerName, apiKey };
        const response = await axios.put(url, payload, {
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            validateStatus: (status) => status >= 200 && status < 500,
        });
        if (response.status === 200) {
            console.debug(`[upsertUserApiKey] Success for provider ${providerName}.`);
            return { status: true, message: response.data.message };
        } else {
             return handleApiError({ response }, 'upsertUserApiKey');
        }
    } catch (error) {
        return handleApiError(error, 'upsertUserApiKey');
    }
};

/**
 * Deletes the API key for the current user and a specific provider.
 * @param {string} token - Firebase Auth ID token.
 * @param {string} providerName - The name of the LLM provider whose key to delete.
 * @returns {Promise<{status: boolean, message: string}|{status: boolean, message: string, error?: any}>}
 */
export const deleteUserApiKey = async (token, providerName) => {
    const url = `${configApiUrl}/user-api-key/${encodeURIComponent(providerName)}`;
    console.debug(`[deleteUserApiKey] Attempting delete for provider: ${providerName}`);
    try {
        const response = await axios.delete(url, {
            headers: { Authorization: `Bearer ${token}` },
            validateStatus: (status) => status >= 200 && status < 500, // Handle 404 correctly
        });
        if (response.status === 200) {
            console.debug(`[deleteUserApiKey] Success for provider ${providerName}.`);
            return { status: true, message: response.data.message };
        } else {
             // handleApiError will process 404, 400, 500 etc.
             return handleApiError({ response }, 'deleteUserApiKey');
        }
    } catch (error) {
        // Handle network errors etc.
        return handleApiError(error, 'deleteUserApiKey');
    }
};

/** Admin ONLY LLM configurations API endpoint calls **/

/** Admin: Fetch ALL LLM configurations (including unavailable). */
export const fetchAdminLlmConfigurations = async (token) => {
    const url = `${configApiUrl}/configurations`;
    try {
        const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` }, validateStatus: (s) => s < 500 });
        if (response.status === 200) {
            // Map keys for frontend consistency
            const configs = response.data.map(c => ({
                llmConfigId: c.llm_config_id ?? '',
                providerName: c.provider_name ?? '',
                modelName: c.model_name ?? '',
                idiom: c.idiom ?? '',
                isAvailable: c.is_available ?? '',
            }));
            return { status: true, data: configs };
        } else { return handleApiError({ response }, 'fetchAdminLlmConfigurations'); }
    } catch (error) { return handleApiError(error, 'fetchAdminLlmConfigurations'); }
};

/** Admin: Add new LLM configuration(s). */
export const addLlmConfiguration = async (token, providerName, modelName, idioms, isAvailable) => {
    const url = `${configApiUrl}/configurations`;
    const payload = { providerName, modelName, idioms, isAvailable };
    try {
        const response = await axios.post(url, payload, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, validateStatus: (s) => s < 500 });
        // Handle 201 (Created) and 207 (Multi-Status)
        if (response.status === 201 || response.status === 207) {
            // Map added items keys if needed
            const added = response.data.added?.map(c => ({
                llmConfigId: c.llmConfigId ?? '', // Assuming backend sends camelCase or map here
                providerName: c.providerName ?? '',
                modelName: c.modelName ?? '',
                idiom: c.idiom ?? '',
                isAvailable: c.isAvailable ?? '',
            })) || [];
            return { status: true, message: response.data.message, added, errors: response.data.errors, statusCode: response.status };
        } else { return handleApiError({ response }, 'addLlmConfiguration'); }
    } catch (error) { return handleApiError(error, 'addLlmConfiguration'); }
};

/** Admin: Update a specific LLM configuration. */
export const updateLlmConfiguration = async (token, llmConfigId, updateData) => {
    // updateData = { providerName?, modelName?, idiom?, isAvailable? }
    const url = `${configApiUrl}/configurations/${llmConfigId}`;
    try {
        const response = await axios.put(url, updateData, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, validateStatus: (s) => s < 500 });
        if (response.status === 200) {
             // Map updated item keys if needed
             const updated = response.data.updated ? {
                llmConfigId: response.data.updated.llmConfigId, // Assuming backend sends camelCase or map here
                providerName: response.data.updated.providerName,
                modelName: response.data.updated.modelName,
                idiom: response.data.updated.idiom,
                isAvailable: response.data.updated.isAvailable,
             } : null;
            return { status: true, message: response.data.message, updated };
        } else { return handleApiError({ response }, 'updateLlmConfiguration'); }
    } catch (error) { return handleApiError(error, 'updateLlmConfiguration'); }
};

/** Admin: Delete a specific LLM configuration. */
export const deleteLlmConfiguration = async (token, llmConfigId) => {
    const url = `${configApiUrl}/configurations/${llmConfigId}`;
    try {
        const response = await axios.delete(url, { headers: { Authorization: `Bearer ${token}` }, validateStatus: (s) => s < 500 });
        if (response.status === 200) { return { status: true, message: response.data.message }; }
        else { return handleApiError({ response }, 'deleteLlmConfiguration'); }
    } catch (error) { return handleApiError(error, 'deleteLlmConfiguration'); }
};